import React from 'react';
import Icon from '../../../components/AppIcon';

const ActivityItem = ({ activity, userRole = 'dentist_client', currentUserId = null }) => {
  const getActivityIcon = (type) => {
    const icons = {
      case_created: 'FileText',
      case_updated: 'Edit',
      message: 'MessageSquare',
      incident: 'AlertTriangle',
      payment: 'CreditCard',
      file_upload: 'Upload',
    };
    return icons?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colors = {
      case_created: 'var(--color-primary)',
      case_updated: 'var(--color-accent)',
      message: 'var(--color-secondary)',
      incident: 'var(--color-warning)',
      payment: 'var(--color-success)',
      file_upload: 'var(--color-primary)',
    };
    return colors?.[type] || 'var(--color-muted-foreground)';
  };

  // French activity type labels
  const getActivityTypeLabel = (type) => {
    const labels = {
      case_created: 'Cas créé',
      case_updated: 'Cas mis à jour',
      message: 'Message',
      incident: 'Incident',
      payment: 'Paiement',
      file_upload: 'Fichier uploadé',
    };
    return labels?.[type] || 'Activité';
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    return `Il y a ${diffDays} jours`;
  };

  const isAdmin = userRole === 'administrator';
  const isOwnActivity = activity?.user?.id === currentUserId;

  // For non-admins, only render activity if it belongs to the current user
  if (!isAdmin && !isOwnActivity) {
    return null;
  }

  // French titles: personalize for own activity
  const getDisplayTitle = () => {
    if (isAdmin) return activity?.title;

    return activity?.title
      ?.replace('Nouvelle conception créée', 'Votre conception créée')
      ?.replace('Nouvelle', 'Votre')
      ?.replace('Nouveau message', 'Votre message')
      ?.replace('Incident signalé', 'Incident que vous avez signalé');
  };

  const getDisplayDescription = () => {
    return activity?.description;
  };

  return (
    <div className="flex items-start gap-4 p-4 hover:bg-muted/50 rounded-lg transition-colors duration-200">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${getActivityColor(activity?.type)}15` }}
      >
        <Icon name={getActivityIcon(activity?.type)} size={18} color={getActivityColor(activity?.type)} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">{getDisplayTitle()}</p>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${getActivityColor(activity?.type)}15`,
                color: getActivityColor(activity?.type),
              }}
            >
              {getActivityTypeLabel(activity?.type)}
            </span>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
            {formatTime(activity?.timestamp)}
          </span>
        </div>

        <p className="text-sm text-muted-foreground mb-2">{getDisplayDescription()}</p>

        {/* Admin: show who performed the action */}
        {isAdmin && activity?.user?.name && (
          <div className="flex items-center gap-1 mt-1">
            <Icon name="User" size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Par : <span className="font-medium text-foreground">{activity.user.name}</span>
            </span>
          </div>
        )}

        {/* Non-admin own activity: subtle "Vous" badge */}
        {!isAdmin && isOwnActivity && (
          <div className="flex items-center gap-1 mt-1">
            <Icon name="User" size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Par : <span className="font-medium text-foreground">Vous</span></span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityItem;